import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Button, Cushion, Input, MaxWidth, Space, Typography } from '@pitchero/react-ui';
import LoadingBar from '../loading-bar';
import PitcheroLogo from '../pitchero-logo';
import { Trans } from '../../lib/i18n';
import { resetPassword } from '../../store/auth/actions';

import {
  isSubmittingPasswordReset,
  passwordResetHasFailed,
  passwordResetStatus,
} from '../../store/auth/selectors';

const PasswordResetForm = ({
  isSubmittingRequest,
  requestReset,
  hasFailedPasswordReset,
  resetStatus,
}) => {
  const [email, setEmail] = useState('');
  const [linkSent, setLinkSent] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    if (mounted && !isSubmittingRequest) {
      setLinkSent(true);
    }

    setMounted(true);
  }, [isSubmittingRequest]);

  const renderInput = () => (
    <>
      <Typography preset="body--small" component="p">
        <Trans i18nKey="common:password_reset_instruction">
          Enter your email address and we’ll send you a link so you can reset your password.
        </Trans>
      </Typography>

      <Space bottom="xsmall">
        <Typography
          preset="description"
          component="label"
          htmlFor="email"
          align="left"
          color="dustyGrey"
        >
          <Trans i18nKey="common:login_email_label">Email Address</Trans>
        </Typography>
      </Space>

      <Space bottom="medium">
        <Input
          type="email"
          name="email"
          disabled={isSubmittingRequest}
          input={{ value: email }}
          onChange={(event) => setEmail(event.target.value)}
        />
      </Space>

      <Space bottom="large">
        <Button
          fullWidth
          theme="primary"
          type="submit"
          disabled={isSubmittingRequest || email.length === 0}
          onClick={() => requestReset(email)}
        >
          <Trans i18nKey="common:password_reset_cta">Send reset link</Trans>
        </Button>
      </Space>
    </>
  );

  const renderSent = () => {
    const isInMaintenance = hasFailedPasswordReset && resetStatus === 503;

    if (hasFailedPasswordReset) {
      return (
        <Space bottom="medium">
          <Typography color="red" preset="body--small" component="p" fontWeight="bold">
            <Trans i18nKey="common:error_code" values={{ error: resetStatus }} />
          </Typography>

          <Typography color="red" preset="body--small" component="p">
            <Trans i18nKey={`common:password_reset_error_${resetStatus}`}>
              An error occurred when trying to send the password reset link. Please try again later.
            </Trans>
          </Typography>

          {!isInMaintenance && (
            <Button preset="text" onClick={() => setLinkSent(false)}>
              <Typography preset="button" color="primary">
                <Trans i18nKey="common:password_reset_resend">Resend link</Trans>
              </Typography>
            </Button>
          )}
        </Space>
      );
    }

    return (
      <Space bottom="medium">
        <Typography preset="body--small" component="p">
          <Trans i18nKey="common:password_reset_confirmation">
            A password reset link has been sent to the email address provided.
          </Trans>
        </Typography>
        <Button preset="text" onClick={() => setLinkSent(false)}>
          <Typography preset="button" color="primary">
            <Trans i18nKey="common:password_reset_resend">Resend link</Trans>
          </Typography>
        </Button>
      </Space>
    );
  };

  return (
    <Typography align="center" component="div">
      <LoadingBar isLoading={isSubmittingRequest} />
      <Cushion vertical="xlarge" horizontal="large" component="div">
        <MaxWidth component="div" maxWidth={272} center>
          <Space bottom="medium">
            <Typography
              preset="title"
              component="h2"
              color={hasFailedPasswordReset ? 'red' : 'black'}
            >
              <Trans i18nKey="common:password_reset_title">Forgotten your password</Trans>
            </Typography>
          </Space>

          {linkSent ? renderSent() : renderInput()}

          <Space bottom="xsmall">
            <Typography color="block" preset="tag--large" align="center" component="div">
              <Trans i18nKey="common:powered_by">Powered by</Trans>
            </Typography>
          </Space>
          <PitcheroLogo />
        </MaxWidth>
      </Cushion>
    </Typography>
  );
};

PasswordResetForm.propTypes = {
  isSubmittingRequest: PropTypes.bool.isRequired,
  requestReset: PropTypes.func.isRequired,
  hasFailedPasswordReset: PropTypes.bool.isRequired,
  resetStatus: PropTypes.number.isRequired,
};

const mapStateToProps = (state) => ({
  isSubmittingRequest: isSubmittingPasswordReset(state),
  hasFailedPasswordReset: passwordResetHasFailed(state),
  resetStatus: passwordResetStatus(state),
});

// Method to reset password, from '../../store/auth/actions'
export default connect(mapStateToProps, { requestReset: resetPassword })(PasswordResetForm);
